import React from "react";
import { cn } from "@/lib/utils"

function Skeleton({
  className,
  ...props
}: React.HTMLAttributes<HTMLDivElement>) {
  return (
    <div
      className={cn("animate-pulse rounded-md bg-muted", className)}
      {...props}
    />
  )
}

// OPTIMIZATION PHASE 2.5: Specialized skeleton components for progressive loading

// Project Card Skeleton
export const ProjectCardSkeleton: React.FC = () => (
  <div className="hover:shadow-lg transition-shadow border rounded-lg p-6">
    <div className="flex justify-between items-start mb-4">
      <Skeleton className="h-6 w-3/5" />
      <Skeleton className="h-5 w-20" />
    </div>
    
    <div className="space-y-4">
      {/* Progress section */}
      <div>
        <div className="flex justify-between text-sm mb-2">
          <Skeleton className="h-4 w-15" />
          <Skeleton className="h-4 w-8" />
        </div>
        <Skeleton className="h-2 w-full rounded-full" />
      </div>
      
      {/* Progress breakdown */}
      <div className="grid grid-cols-2 gap-2">
        {[1, 2, 3, 4].map(i => (
          <div key={i} className="flex justify-between">
            <Skeleton className="h-3 w-12" />
            <Skeleton className="h-3 w-6" />
          </div>
        ))}
      </div>
      
      {/* Chapter progress */}
      <div>
        <Skeleton className="h-4 w-30 mb-2" />
        <Skeleton className="h-1 w-full rounded-full" />
      </div>
      
      {/* Actions */}
      <div className="flex justify-between items-center mt-4">
        <Skeleton className="h-3 w-25" />
        <div className="flex gap-2">
          <Skeleton className="h-8 w-15" />
          <Skeleton className="h-8 w-30" />
        </div>
      </div>
    </div>
  </div>
);

// Chapter Content Skeleton
export const ChapterContentSkeleton: React.FC = () => (
  <div className="space-y-4">
    {/* Chapter header */}
    <div className="text-center mb-8">
      <Skeleton className="h-4 w-25 mx-auto mb-2" />
      <Skeleton className="h-8 w-50 mx-auto" />
    </div>
    
    {/* Content paragraphs */}
    {[1, 2, 3, 4].map(i => (
      <div key={i} className="space-y-2">
        <Skeleton className="h-4 w-full" />
        <Skeleton className="h-4 w-full" />
        <Skeleton className="h-4 w-4/5" />
      </div>
    ))}
    
    {/* Show more button placeholder */}
    <div className="text-center mt-6">
      <Skeleton className="h-10 w-30 mx-auto" />
    </div>
  </div>
);

// Blueprint Loading Skeleton
export const BlueprintSkeleton: React.FC = () => (
  <div className="space-y-6 p-6">
    <Skeleton className="h-6 w-50" />
    
    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
      {[1, 2, 3, 4].map(i => (
        <div key={i} className="space-y-3">
          <Skeleton className="h-5 w-30" />
          <Skeleton className="h-25 w-full" />
        </div>
      ))}
    </div>
    
    <div className="space-y-3">
      <Skeleton className="h-5 w-37" />
      {[1, 2, 3].map(i => (
        <div key={i} className="flex items-center space-x-3">
          <Skeleton className="h-6 w-6 rounded-full" />
          <Skeleton className="h-4 flex-1" />
        </div>
      ))}
    </div>
  </div>
);

// Dashboard Stats Skeleton
export const StatsSkeleton: React.FC = () => (
  <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
    {[1, 2, 3, 4].map(i => (
      <div key={i} className="p-6 border rounded-lg">
        <div className="flex items-center gap-4">
          <Skeleton className="h-12 w-12 rounded-lg" />
          <div className="space-y-2">
            <Skeleton className="h-6 w-10" />
            <Skeleton className="h-3 w-20" />
          </div>
        </div>
      </div>
    ))}
  </div>
);

export { Skeleton }